import { type ComponentType, type ReactNode } from 'react';
import { cn } from '@/lib/utils/cn';

import { Card, CardContent } from '@/components/ui/card';

/*
 * KPICard (composed, screen-3). A domain-free metric card: an optional leading
 * icon, a label, and a value. When the value is null/undefined/empty, a muted
 * em dash renders in its place. Data via props — the feature supplies the
 * already-formatted value string (money/number formatting lives in lib/format).
 */

export function KPICard({
  label,
  value,
  icon: Icon,
  hint,
  className,
}: {
  label: string;
  /** Pre-formatted display value; empty/nullish renders a muted "—". */
  value?: ReactNode;
  icon?: ComponentType<{ className?: string }>;
  /** Optional secondary line under the value (e.g. delta, period). */
  hint?: string;
  className?: string;
}) {
  const isEmpty =
    value == null || (typeof value === 'string' && value.trim() === '');

  return (
    <Card size="sm" className={cn(className)}>
      <CardContent className="flex items-start gap-3">
        {Icon ? (
          <span
            aria-hidden
            className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground"
          >
            <Icon className="size-5" />
          </span>
        ) : null}
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm text-muted-foreground">{label}</span>
          <span
            data-slot="kpi-value"
            data-empty={isEmpty}
            className={cn(
              'text-2xl font-semibold tabular-nums',
              isEmpty && 'text-muted-foreground',
            )}
          >
            {isEmpty ? '—' : value}
          </span>
          {hint ? (
            <span className="text-xs text-muted-foreground">{hint}</span>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
